import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconScale } from "@tabler/icons-react";
import { SearchBar } from "../../../../components/common/input";
import { Heading, Text } from "../../../../components/common/typography";
import { Pagination } from "../../../../components/common/pagination";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { AppRoutes } from "../../../../common/routes/app_routes";
import { useMyComparisons } from "../../hooks/useMyComparisons";
import { ComparisonListItem } from "../components/comparison-list-item";
import { SortDropdown, sortValueToQuery, type SortValue } from "../components/sort-dropdown";
import type { SavedComparisonDto } from "../../data/comparison.types";

const PAGE_SIZE = 10;

export function MyComparisonsPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [sortValue, setSortValue] = useState<SortValue>("newest");
  const [page, setPage] = useState(1);

  const query = useMemo(() => {
    const { sortBy, sortOrder } = sortValueToQuery(sortValue);
    return {
      page,
      pageSize: PAGE_SIZE,
      search: search.trim() || undefined,
      sortBy,
      sortOrder,
    };
  }, [page, search, sortValue]);

  const { data, isLoading } = useMyComparisons(query);

  const handleView = (comparison: SavedComparisonDto) => {
    navigate(`${AppRoutes.PRODUCTS_COMPARE}?savedId=${encodeURIComponent(comparison.id)}`);
  };

  const pageCount = data?.meta?.pageCount ?? 1;
  const items = data?.data ?? [];
  const showEmpty = !isLoading && items.length === 0;

  return (
    <div className="flex w-full flex-col items-center gap-6 px-20 py-10 max-md:gap-4 max-md:px-4 max-md:py-6">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col gap-2">
          <Heading variant="h2" tone="dark">
            My Comparisons
          </Heading>
          <Text variant="bodySm" tone="muted">
            View and manage your comparisons
          </Text>
        </div>
      </div>

      <div className="flex w-full items-center gap-12 max-md:gap-3">
        <SearchBar
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search"
          wrapperClassName="flex-1 max-md:min-w-0"
        />
        <SortDropdown
          value={sortValue}
          onChange={(v) => {
            setSortValue(v);
            setPage(1);
          }}
        />
      </div>

      <div className="flex w-full flex-col gap-3">
        {isLoading ? (
          <div className="flex min-h-[240px] items-center justify-center">
            <ShimmerLoader message="Fetching your comparisons…" lines={4} className="max-w-lg" />
          </div>
        ) : showEmpty ? (
          <div className="flex min-h-[240px] flex-col items-center justify-center gap-3 rounded-2xl border border-borderDefault bg-bgWhite p-10 text-center">
            <IconScale size={40} className="text-textMuted" />
            <Heading variant="h3" tone="dark">
              {search.trim() ? "No comparisons match your search" : "No saved comparisons yet"}
            </Heading>
            <Text variant="bodySm" tone="muted">
              {search.trim()
                ? "Try a different search term."
                : "Compare two or three products and save the result to see it here."}
            </Text>
          </div>
        ) : (
          items.map((comparison) => (
            <ComparisonListItem
              key={comparison.id}
              comparison={comparison}
              onView={handleView}
            />
          ))
        )}
      </div>

      {!isLoading && items.length > 0 ? (
        <div className="flex w-full items-center justify-center py-2">
          <Pagination
            page={page}
            pageCount={pageCount}
            onPageChange={setPage}
            aria-label="Comparisons pagination"
          />
        </div>
      ) : null}
    </div>
  );
}

export default MyComparisonsPage;
